import DataTable from "Components/UI/DataTable";
import {
  styled,
  Box,
  Divider,
  SxProps,
  Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";
import { IChartData, TChartData } from "Interfaces";
import {
  attributesFromChartDataRow,
  buildTicks,
  isDimension,
  isJourneyDimension,
  isTrendDimension,
} from "..//utils/utils";
import AnalyticsCell from "../components/AnalyticsCell";
import AnalyticsUserJourneyColumns from "../components/AnalyticsUserJourneyColumns";
import { formatDimension } from "../services/dimensionsFormatter";
import {
  ApiChartAttribute,
  ApiDimension,
  ApiMetric,
  ApiTableGrouping,
} from "@incendium/api";
import { percentageChange } from "Helpers/percentage";
import moment from "moment";
import useMetricExplorerNavigation from "../hooks/useMetricExplorerNavigation";
import useDimensionExplorerNavigation from "../hooks/useDimensionExplorerNavigation";
import useFormatMetric from "../hooks/useFormatMetric";
import { ICompare, IOnClickMap, TTableCustomCellRender } from "../types/types";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { createAnalyticsSortableHeader } from "./AnalyticsSortableHeader";
import { useIsMobile } from "Hooks/useIsMobile";

const StyledWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: -16,
  right: -17,
  top: 0,
  bottom: 0,
}));

interface IAnalyticsDataTableProps {
  data: TChartData[];
  chartAttributes: ApiChartAttribute[];
  totals?: TChartData;
  comparisonTotals?: TChartData;
  onClick?: {
    [field: string]:
      | ((v: string, o?: string) => void | Promise<void>)
      | IOnClickMap[];
  };
  pageSize?: number;
  comparison?: boolean;
  comparisonChartData?: IChartData;
  colWidths?: { [d: string]: number };
  pinAt?: number;
  disableMetricClick?: boolean;
  disableDimensionClick?: boolean;
  sx?: SxProps<Theme> | undefined;
  tableGroupings?: ApiTableGrouping[];
  tableCustomCellRender?: TTableCustomCellRender;
  tableCustomHeaderRender?: TTableCustomCellRender;
  tableHeaderAlign?: Partial<
    Record<ApiDimension | ApiMetric | string, "left" | "center">
  >;
  hiddenCols?: (ApiMetric | ApiDimension)[];
  nonSortableCols?: (ApiMetric | ApiDimension | string)[];
  tableUnpinnedCols?: (ApiMetric | ApiDimension | string)[];
  onVisibleRowsChange?: (rows: any[]) => void;
}

function AnalyticsDataTable({
  data,
  chartAttributes,
  totals,
  onClick,
  comparison,
  comparisonChartData,
  comparisonTotals,
  pageSize,
  colWidths,
  pinAt,
  disableMetricClick,
  disableDimensionClick,
  sx,
  tableGroupings,
  tableCustomCellRender,
  tableCustomHeaderRender,
  tableHeaderAlign,
  hiddenCols,
  nonSortableCols,
  tableUnpinnedCols,
  onVisibleRowsChange,
}: IAnalyticsDataTableProps) {
  const [columns, setColumns] = useState<(ColDef | ColGroupDef)[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [totalRow, setTotalRow] = useState<any[] | undefined>(undefined);
  const formatMetric = useFormatMetric();
  const metricToName = useMetricName();
  const dimensionToName = useDimensionName();
  const onMetricClick = useMetricExplorerNavigation();
  const onDimensionClick = useDimensionExplorerNavigation();
  const isMobile = useIsMobile();

  const tickedComparisonChartData = useMemo(() => {
    return buildTicks(comparisonChartData?.data || []);
  }, [comparisonChartData]);

  // map of Comparison ticks to improve performance for find comparison
  const tickedComparisonMap = useMemo(() => {
    const map = new Map();
    tickedComparisonChartData.forEach((d) => {
      map.set(d.tick, d);
    });
    return map;
  }, [tickedComparisonChartData]);

  const cellRenderer = useCallback(
    (key, { valueFormatted, value, colDef, data, ...f }, colIsDimension) => {
      let compare: ICompare = {};
      let found: TChartData | undefined;
      if (comparison && tickedComparisonMap.size > 0) {
        if (data.id === "Total") {
          found = comparisonTotals;
        } else {
          found = tickedComparisonMap.get(data.tick);
        }

        if (!colIsDimension && found && typeof value === "number") {
          let change = percentageChange(
            Number(found[colDef.field]) || 0,
            value || 0,
          );
          compare = {
            direction: change > 0 ? "up" : change < 0 ? "down" : "same",
            value: Number(change) || 0,
            formatedValue: formatMetric(
              key as ApiMetric,
              Number(found[colDef.field]) || 0,
              { isKM: isMobile },
            ),
          };
        }
      }

      const handler = onClick && onClick[key];
      let fn: IOnClickMap[] = [];

      if (handler) {
        if (typeof handler === "function") {
          fn.push({
            text: "Click To Explore",
            fn: () => handler(value),
          });
        } else if (Array.isArray(handler)) {
          fn = [...handler];
        }
      }

      const metricClickText = `View in Metric Explorer`;
      if (
        !isMobile &&
        !colIsDimension &&
        !disableMetricClick &&
        fn.findIndex((d) => d.text === metricClickText) < 0
      ) {
        fn.push({
          text: metricClickText,
          fn: () =>
            onMetricClick(key, [
              ...chartAttributes,
              ...attributesFromChartDataRow(data),
            ]),
        });
      }

      const clickText = `Explore ${dimensionToName(key as ApiDimension)}`;
      if (
        !isMobile &&
        colIsDimension &&
        !disableDimensionClick &&
        fn.findIndex((d) => d.text === clickText) < 0
      ) {
        fn.push({
          text: clickText,
          fn: () => onDimensionClick(key, [...chartAttributes]),
        });
      }

      return colIsDimension &&
        isJourneyDimension(key as ApiDimension) &&
        data.id !== "Total" ? (
        <AnalyticsUserJourneyColumns
          formatedValue={valueFormatted}
          onClick={typeof handler === "function" ? handler : undefined}
        />
      ) : (
        <AnalyticsCell
          dimensionName={data.name}
          valueFormatted={valueFormatted}
          onClick={fn}
          compare={compare}
          headerName={colDef.headerName}
          isDimension={colIsDimension}
          rowFilters={[...chartAttributes, ...attributesFromChartDataRow(data)]}
        />
      );
    },
    [
      onClick,
      formatMetric,
      onMetricClick,
      onDimensionClick,
      disableMetricClick,
      disableDimensionClick,
      chartAttributes,
      tickedComparisonMap,
      comparisonTotals,
      comparison,
      dimensionToName,
      isMobile,
    ],
  );

  // Store cellRenderer in a ref to avoid including it in useEffect deps
  // This prevents infinite loops when labels load asynchronously on first login
  const cellRendererRef = useRef(cellRenderer);
  useEffect(() => {
    cellRendererRef.current = cellRenderer;
  }, [cellRenderer]);

  // Store metricToName and dimensionToName in refs to prevent effect loops
  // Even after labels load, these callbacks can change if the underlying arrays get new references
  // Using refs ensures the effect doesn't re-run unnecessarily
  const metricToNameRef = useRef(metricToName);
  const dimensionToNameRef = useRef(dimensionToName);
  useEffect(() => {
    metricToNameRef.current = metricToName;
    dimensionToNameRef.current = dimensionToName;
  }, [metricToName, dimensionToName]);

  // Store formatters in refs to create stable function references for AgGrid
  // This prevents AgGrid from treating columns as changed when formatters are recreated
  const formatMetricRef = useRef(formatMetric);
  const formatDimensionRef = useRef(formatDimension);
  useEffect(() => {
    formatMetricRef.current = formatMetric;
    formatDimensionRef.current = formatDimension;
  }, [formatMetric]);

  // Create stable value formatter functions using refs
  // These functions will have stable references but use the latest formatter functions
  const createValueFormatter = useCallback(
    (field: string, colIsDimension: boolean) => {
      return ({ value }: { value: any }) => {
        return colIsDimension
          ? formatDimensionRef.current(field as ApiDimension, value)
          : value === "..."
            ? "..."
            : formatMetricRef.current(field as ApiMetric, value, {
                isKM: isMobile,
              });
      };
    },
    [isMobile],
  );

  // Create stable cell renderer wrapper using refs
  const createCellRenderer = useCallback(
    (field: string, colIsDimension: boolean) => {
      return (params: any) =>
        cellRendererRef.current(field, params, colIsDimension);
    },
    [],
  );

  // Create stable comparator function
  const createDateComparator = useCallback(() => {
    return (date1: string, date2: string) => moment(date1).diff(moment(date2));
  }, []);

  // Cache formatters by field to reuse stable references
  const formatterCacheRef = useRef<
    Map<string, (params: { value: any }) => string>
  >(new Map());
  const cellRendererCacheRef = useRef<Map<string, (params: any) => any>>(
    new Map(),
  );
  const comparatorCacheRef = useRef<
    Map<string, (a: string, b: string) => number>
  >(new Map());
  const headerComponentCacheRef = useRef<
    Map<string, ReturnType<typeof createAnalyticsSortableHeader>>
  >(new Map());

  const tickedData = useMemo(() => buildTicks(data), [data]);

  useEffect(() => {
    if (!tickedData || !tickedData[0]) {
      setRows([]);
      setTotalRow(undefined);
      return;
    }

    // Clear formatter caches when fields change to prevent stale formatters
    // Get current fields from tickedData
    const currentFields = new Set(
      Object.keys(tickedData[0]).filter(
        (field) => !["name", "tick"].includes(field),
      ),
    );

    // Remove formatters for fields that no longer exist
    const fieldsToRemove: string[] = [];
    formatterCacheRef.current.forEach((_, field) => {
      if (!currentFields.has(field)) {
        fieldsToRemove.push(field);
      }
    });
    fieldsToRemove.forEach((field) => {
      formatterCacheRef.current.delete(field);
      cellRendererCacheRef.current.delete(field);
      comparatorCacheRef.current.delete(field);
    });

    headerComponentCacheRef.current.clear();

    // Common function to create a column definition
    const createColDef = (field: string): ColDef => {
      const colIsDimension = isDimension(field);
      const isSortable = !nonSortableCols?.includes(field);
      // Use refs to get the latest name functions without triggering effect re-runs
      let headerName = colIsDimension
        ? dimensionToNameRef.current(field as ApiDimension)
        : metricToNameRef.current(field as ApiMetric);

      // Get or create stable formatter functions from cache
      // This ensures AgGrid sees the same function reference even when the effect re-runs
      let valueFormatter = formatterCacheRef.current.get(field);
      if (!valueFormatter) {
        valueFormatter = createValueFormatter(field, colIsDimension);
        formatterCacheRef.current.set(field, valueFormatter);
      }

      let cellRenderer = cellRendererCacheRef.current.get(field);
      if (!cellRenderer) {
        cellRenderer = createCellRenderer(field, colIsDimension);
        cellRendererCacheRef.current.set(field, cellRenderer);
      }

      let comparator: ((date1: string, date2: string) => number) | undefined;
      if (isTrendDimension(field as ApiDimension)) {
        comparator = comparatorCacheRef.current.get(field);
        if (!comparator) {
          comparator = createDateComparator();
          comparatorCacheRef.current.set(field, comparator);
        }
      }

      const customHeaderRender = tableCustomHeaderRender?.[field];
      const headerAlign =
        tableHeaderAlign?.[field] ?? (colIsDimension ? "left" : "center");
      const headerCacheKey = `${field}:${headerAlign}`;
      let headerComponent = headerComponentCacheRef.current.get(headerCacheKey);
      if (customHeaderRender && !headerComponent) {
        headerComponent = createAnalyticsSortableHeader(
          customHeaderRender,
          headerAlign,
        );
        headerComponentCacheRef.current.set(headerCacheKey, headerComponent);
      }

      return {
        headerName,
        field,
        sortable: isSortable,
        headerClass:
          headerAlign === "left" ? "ag-header-align-left" : undefined,
        pinned: pinAt
          ? tickedData[0][field] < pinAt
            ? "left"
            : undefined
          : colIsDimension && !tableUnpinnedCols?.includes(field)
            ? "left"
            : undefined,
        minWidth:
          colWidths && colWidths[`${field}`]
            ? colWidths[`${field}`]
            : colIsDimension
              ? isJourneyDimension(field as ApiDimension)
                ? 500
                : 200
              : isMobile
                ? 100
                : 130,
        width:
          colWidths && colWidths[`${field}`]
            ? colWidths[`${field}`]
            : undefined,
        valueFormatter,
        headerComponent: customHeaderRender ? headerComponent : undefined,
        cellRenderer:
          tableCustomCellRender && tableCustomCellRender[field]
            ? tableCustomCellRender[field]
            : cellRenderer,
        comparator,
      } as ColDef;
    };

    // Generate all columns with order maintained
    const cols: (ColDef | ColGroupDef)[] = [];
    const usedFields = new Set<string>();

    Object.keys(tickedData[0])
      .filter((field) => !["name", "tick"].includes(field)) // Exclude unwanted fields
      .filter(
        (field) => !hiddenCols?.includes(field as ApiDimension | ApiMetric),
      )
      .forEach((field) => {
        // Check if the metric belongs to a group
        const group = (tableGroupings || []).find((group) =>
          (group.metrics || []).includes(field as ApiMetric),
        );
        if (group) {
          // Check if the group is already added
          let groupCol = cols.find(
            (col) => "headerName" in col && col.headerName === group.name,
          ) as ColGroupDef;

          if (!groupCol) {
            // Create a new group if not present
            groupCol = {
              headerName: group.name,
              children: [],
              headerGroupComponent:
                tableCustomHeaderRender &&
                tableCustomHeaderRender[group.name as string]
                  ? tableCustomHeaderRender[group.name as string]
                  : undefined,
            } as ColGroupDef;
            cols.push(groupCol);
          }

          // Add the field to the group
          groupCol.children.push(createColDef(field));
          usedFields.add(field);
        } else if (!usedFields.has(field)) {
          // Add ungrouped fields directly
          cols.push(createColDef(field));
          usedFields.add(field);
        }
      });

    const rows = (tickedData || []).map((v, i) => {
      return {
        id: i,
        ...v,
      };
    });

    setColumns(cols);
    setRows(rows);
    if (totals) {
      setTotalRow([totals]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tickedData,
    totals,
    comparison,
    comparisonChartData,
    // cellRenderer, metricToName, and dimensionToName are intentionally excluded
    // They are stored in refs and updated via separate useEffects to prevent infinite loops
    // when labels load asynchronously or when array references change in Redux state
    colWidths,
    pinAt,
    formatMetric,
    comparisonTotals,
    tableGroupings,
    tableCustomCellRender,
    tableCustomHeaderRender,
    tableHeaderAlign,
    hiddenCols,
    nonSortableCols,
    tableUnpinnedCols,
    isMobile,
  ]);

  return (
    <StyledWrapper sx={sx}>
      <Divider />
      <DataTable
        colDefs={columns}
        rows={rows}
        totalRow={totalRow}
        pageSize={pageSize}
        onVisibleRowsChange={onVisibleRowsChange}
      />
    </StyledWrapper>
  );
}

export default AnalyticsDataTable;
